import { useCallback, useEffect, useRef, useState } from 'react';
import { createPopper, Instance, Options } from '@popperjs/core/lib/popper-lite';
import { useOutsideClick } from '../../hooks/useOutsideClick';
import { isBrowser, isMobile } from '../../utils';

interface UsePopper {
  triggerRef: React.RefObject<HTMLElement>;
  popupRef: React.RefObject<HTMLDivElement>;
  innerRef: React.RefObject<HTMLDivElement>;
  isOpen: boolean;
  showHandler: () => void;
  hideHandler: () => void;
  create: () => void;
  destroy: () => void;
}

export function usePopper(
  popperConfig?: Options,
  updateProps: any[] = [],
  targetRef?: React.RefObject<HTMLElement>
): UsePopper {
  const [isOpen, setIsOpen] = useState(false);
  const popperInstance = useRef<Instance | null>();

  const triggerRef = useRef<HTMLElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);

  function onPopState(event: PopStateEvent) {
    //@ts-ignore
    if (!event.singleSpa) setIsOpen(false);
    //  single-spa тригерит событие popstate в ответ на pushState()
  }

  function create() {
    if (triggerRef.current && popupRef.current) {
      const popper = createPopper(
        targetRef?.current ?? triggerRef.current,
        popupRef.current,
        popperConfig || {}
      );
      popper.forceUpdate();
      popperInstance.current = popper;
      if (isBrowser) {
        window.addEventListener('popstate', onPopState);
      }
    } else {
      throw new Error(
        'Tooltip: triggerRef.current or popupRef.current are not defined (wrap trigger`s component in React.ForwardRef)'
      );
    }
  }

  function destroy() {
    if (popperInstance.current) {
      popperInstance.current.destroy();
      popperInstance.current = null;
    }
    if (isBrowser) {
      window.removeEventListener('popstate', onPopState);
    }
  }

  const showHandler = useCallback(() => {
    setIsOpen(true);

    if (isMobile() && isBrowser) {
      window.history.pushState(null, '');
    }
  }, [isOpen]);

  const hideHandler = useCallback(() => {
    setIsOpen(false);

    if (isMobile() && isBrowser) {
      window.history.back();
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      destroy();
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      popperInstance.current?.forceUpdate();
    }
  }, [isOpen, popperInstance, ...updateProps]);

  useOutsideClick([innerRef, triggerRef], isOpen, () => {
    hideHandler();
  });

  return {
    triggerRef,
    popupRef,
    innerRef,
    isOpen,
    showHandler,
    hideHandler,
    create,
    destroy
  };
}
